import { normalizeProductName } from "./utils";

/**
 * @typedef {{
 *   productName: string,
 *   color: string,
 *   size: string,
 *   variantLabel: string,
 *   typeLabel: string,
 *   sku: string,
 *   sold: number,
 *   stock: number|null,
 *   mergedSizes?: boolean,
 *   mergedColors?: boolean,
 *   colorsSummary?: string
 * }} SoldLine
 */

function normPropKey(name) {
    return String(name || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

function isColorPropName(name) {
    const k = normPropKey(name);
    return k === "колір" || k === "color" || k === "koлір" || k === "мова";
}

function isSizePropName(name) {
    const k = normPropKey(name);
    return k === "розмір" || k === "розмiр" || k === "size";
}

function isTypePropName(name) {
    return normPropKey(name) === "тип";
}

function isModelPropName(name) {
    const k = normPropKey(name);
    return k === "модель" || k === "model";
}

/**
 * Залишок на офері KeyCRM: quantity − in_reserve (як у WebSocketComponent).
 * Може бути від’ємним при «продажі в мінус».
 * @returns {number|null}
 */
export function offerStockRemain(product) {
    if (!product?.offer || (product.offer.quantity == null && product.offer.in_reserve == null)) {
        return null;
    }
    const q = Number(product.offer.quantity) || 0;
    const r = Number(product.offer.in_reserve) || 0;
    return q - r;
}

/**
 * @param {object} product — рядок `order.products[]`
 * @returns {{ color: string, size: string, typeLabel: string, variantLabel: string }}
 */
export function parseOfferVariant(product) {
    const props = product?.offer?.properties || [];
    let color = "";
    let size = "";
    let typeRaw = "";
    let modelRaw = "";
    for (const p of props) {
        const nm = p?.name;
        const val = String(p?.value || "").trim();
        if (isColorPropName(nm)) {
            color = val;
        } else if (isSizePropName(nm)) {
            size = val;
        } else if (isTypePropName(nm)) {
            typeRaw = val;
        } else if (isModelPropName(nm)) {
            modelRaw = val;
        }
    }
    /** Для розведення рядків: «Тип» або «Модель»; у списку в лапках — спершу «Тип», інакше «Модель». */
    const variantLabel = typeRaw || modelRaw || "";
    return {
        color: color || "—",
        size: size || "—",
        typeLabel: typeRaw,
        variantLabel,
    };
}

/** @deprecated У коді краще `parseOfferVariant`; лишено для сумісності імпортів. */
export function parseOfferColorSize(product) {
    const v = parseOfferVariant(product);
    return { color: v.color, size: v.size };
}

/** Українські лапки-ялинки для підпису варіанта (Тип / Модель). */
export function formatVariantGuillemets(label) {
    const s = String(label || "").trim();
    if (!s) return "";
    return `\u00AB${s}\u00BB`;
}

/**
 * @param {object[]} orders — відфільтровані замовлення
 * @returns {SoldLine[]}
 */
export function collectSoldLinesFromOrders(orders) {
    /** @type {Map<string, SoldLine>} */
    const map = new Map();

    for (const order of orders) {
        if (!order?.products?.length) continue;

        for (const product of order.products) {
            const productName = normalizeProductName(product.name);
            const { color, size, typeLabel, variantLabel } = parseOfferVariant(product);
            const sku = String(product.offer?.sku || product.sku || "").trim() || "—";
            const sold = Number(product.quantity) || 0;
            const stock = offerStockRemain(product);

            const discriminant = variantLabel || sku || "—";
            const key = `${productName}\0${color}\0${size}\0${discriminant}`;
            const prev = map.get(key);
            if (!prev) {
                map.set(key, { productName, color, size, typeLabel, variantLabel, sku, sold, stock });
            } else {
                prev.sold += sold;
                if (prev.stock != null && stock != null) {
                    prev.stock = stock;
                } else if (prev.stock == null && stock != null) {
                    prev.stock = stock;
                }
            }
        }
    }

    return [...map.values()];
}

function sumSold(rows) {
    return rows.reduce((a, r) => a + (Number(r.sold) || 0), 0);
}

function sortUk(a, b) {
    return String(a).localeCompare(String(b), "uk");
}

function cmpSoldDescRows(a, b) {
    const ds = (Number(b.sold) || 0) - (Number(a.sold) || 0);
    if (ds !== 0) return ds;
    return (
        sortUk(a.typeLabel || "", b.typeLabel || "") ||
        sortUk(a.variantLabel || "", b.variantLabel || "") ||
        sortUk(a.size, b.size) ||
        sortUk(a.sku, b.sku)
    );
}

/**
 * @param {string[]} keys
 * @param {(key: string) => SoldLine[]} getRows
 * @param {'sold'|'alphabet'} sortOrder
 */
function sortGroupKeys(keys, getRows, sortOrder) {
    const k = keys.slice();
    if (sortOrder === "alphabet") {
        return k.sort(sortUk);
    }
    return k.sort((a, b) => {
        const sa = sumSold(getRows(a));
        const sb = sumSold(getRows(b));
        if (sb !== sa) return sb - sa;
        return sortUk(a, b);
    });
}

/** Сума залишку, лише якщо для кожного рядка він відомий; інакше null. */
function sumStockStrict(rows) {
    if (!rows.length) return null;
    let total = 0;
    for (const r of rows) {
        if (r.stock == null || Number.isNaN(Number(r.stock))) return null;
        total += Number(r.stock);
    }
    return total;
}

function formatSkuSummary(uniqSkuRaw) {
    const skuDisplay = uniqSkuRaw.filter((x) => x && x !== "—");
    if (skuDisplay.length === 0) return "—";
    if (skuDisplay.length === 1) return skuDisplay[0];
    if (skuDisplay.length <= 4) return skuDisplay.join(", ");
    return `${skuDisplay.slice(0, 3).join(", ")} +${skuDisplay.length - 3}`;
}

/**
 * Об'єднати кілька рядків одного кольору в один (усі розміри).
 */
function mergeRowsAllSizes(rows) {
    const sorted = rows.slice().sort((a, b) => sortUk(a.size, b.size));
    const sold = sumSold(sorted);
    const uniqSku = [...new Set(sorted.map((r) => r.sku))];
    const skuStr = formatSkuSummary(uniqSku);
    const stock = sumStockStrict(sorted);

    const uniqSizes = [...new Set(sorted.map((r) => r.size).filter((s) => s && s !== "—"))].sort(sortUk);
    const mergedSizes = uniqSizes.length > 1;
    const sizeLabel = mergedSizes ? null : uniqSizes[0] || sorted[0].size || "—";

    return {
        productName: sorted[0].productName,
        color: sorted[0].color,
        size: sizeLabel === null ? "—" : sizeLabel,
        variantLabel: "",
        typeLabel: "",
        sku: skuStr,
        sold,
        stock,
        mergedSizes,
    };
}

/**
 * Об'єднати кілька рядків одного розміру (різні кольори) в один рядок.
 */
function mergeRowsAllColors(rows) {
    const sorted = rows.slice().sort((a, b) => sortUk(a.color, b.color));
    const sold = sumSold(sorted);
    const uniqSku = [...new Set(sorted.map((r) => r.sku))];
    const skuStr = formatSkuSummary(uniqSku);

    const uniqColors = [...new Set(sorted.map((r) => r.color).filter((c) => c && c !== "—"))]
        .sort(sortUk);

    let colorsSummary;
    if (uniqColors.length === 0) colorsSummary = "—";
    else if (uniqColors.length <= 3) colorsSummary = uniqColors.join(", ");
    else colorsSummary = `${uniqColors.slice(0, 2).join(", ")} +${uniqColors.length - 2}`;

    const mergedColors = uniqColors.length > 1;
    const stock = sumStockStrict(sorted);

    return {
        productName: sorted[0].productName,
        color: uniqColors.length === 1 ? uniqColors[0] : "—",
        size: sorted[0].size,
        variantLabel: "",
        typeLabel: "",
        sku: skuStr,
        sold,
        stock,
        mergedColors,
        colorsSummary,
    };
}

/**
 * @param {SoldLine[]} lines
 * @param {'none'|'color'|'size'} mode — у межах одного товару: без об'єднання / по кольору (усі розміри) / по розміру (усі кольори)
 * @param {'sold'|'alphabet'} [sortOrder='sold'] — за сумою продажів (спадання) або за назвою (А–Я)
 */
export function buildSoldNestedTree(lines, mode, sortOrder = "sold") {
    if (!lines.length) return [];

    /** @type {Map<string, SoldLine[]>} */
    const byProduct = new Map();
    for (const line of lines) {
        if (!byProduct.has(line.productName)) byProduct.set(line.productName, []);
        byProduct.get(line.productName).push(line);
    }

    const productKeys = [...byProduct.keys()];
    const productNames =
        sortOrder === "alphabet"
            ? productKeys.sort(sortUk)
            : productKeys.sort((a, b) => {
                  const sa = sumSold(byProduct.get(a));
                  const sb = sumSold(byProduct.get(b));
                  if (sb !== sa) return sb - sa;
                  return sortUk(a, b);
              });

    return productNames.map((productName) => {
        const productLines = byProduct.get(productName);

        const distinctSizes = new Set(
            productLines.map((l) => l.size).filter((s) => s && s !== "—")
        );
        /** Один відомий розмір на всю нормалізовану групу — не показуємо «Розмір …» у списку. */
        const hideSizeLabel = distinctSizes.size <= 1;

        if (mode === "size") {
            /** @type {Map<string, SoldLine[]>} */
            const bySize = new Map();
            for (const line of productLines) {
                const k = line.size || "—";
                if (!bySize.has(k)) bySize.set(k, []);
                bySize.get(k).push(line);
            }

            const sizesSorted = sortGroupKeys([...bySize.keys()], (key) => bySize.get(key) || [], sortOrder);
            const sizeBlocks = sizesSorted.map((size) => {
                const rawRows = bySize.get(size);
                return {
                    groupKey: size,
                    rows: [mergeRowsAllColors(rawRows)],
                    totalSold: sumSold(rawRows),
                };
            });

            return {
                mode: "size",
                groupLabel: productName,
                totalSold: sumSold(productLines),
                hideSizeLabel,
                sizeBlocks,
            };
        }

        /** @type {Map<string, SoldLine[]>} */
        const byColor = new Map();
        for (const line of productLines) {
            const k = line.color || "—";
            if (!byColor.has(k)) byColor.set(k, []);
            byColor.get(k).push(line);
        }

        const colorsSorted = sortGroupKeys([...byColor.keys()], (key) => byColor.get(key) || [], sortOrder);

        if (mode === "none") {
            const colorBlocks = colorsSorted.map((color) => {
                const raw = byColor.get(color).slice();
                const rows =
                    sortOrder === "alphabet"
                        ? raw.sort(
                              (a, b) =>
                                  sortUk(a.typeLabel || "", b.typeLabel || "") ||
                                  sortUk(a.variantLabel || "", b.variantLabel || "") ||
                                  sortUk(a.size, b.size) ||
                                  sortUk(a.sku, b.sku)
                          )
                        : raw.sort(cmpSoldDescRows);

                return {
                    groupKey: color,
                    rows,
                    totalSold: sumSold(byColor.get(color)),
                };
            });

            return {
                mode: "none",
                groupLabel: productName,
                totalSold: sumSold(productLines),
                hideSizeLabel,
                colorBlocks,
            };
        }

        /* mode === "color" — у межах товару: один рядок на колір із сумою по всіх розмірах */
        const colorBlocks = colorsSorted.map((color) => {
            const rawRows = byColor.get(color);
            return {
                groupKey: color,
                rows: [mergeRowsAllSizes(rawRows)],
                totalSold: sumSold(rawRows),
            };
        });

        return {
            mode: "color",
            groupLabel: productName,
            totalSold: sumSold(productLines),
            hideSizeLabel,
            colorBlocks,
        };
    });
}
